import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  Send,
  Square,
  Pencil,
  RotateCcw,
  Users,
} from 'lucide-react';
import { inspectionService } from '../../../services/inspectionService';
import { ReportRecipientsEditor } from './ReportRecipientsEditor';
import { SendConfirmSheet } from './SendConfirmSheet';
import { DismissButton } from './DismissButton';
import {
  clientSignatureLine,
  type ClientSignatureSummary,
} from '../clientSignature';
import {
  SEND_POLL_MS,
  formatCountdown,
  sendsAtLabel,
  useCountdown,
  type SendReceipt,
} from '../sendHold';

type Recipient = { email: string; name: string; status?: string };

type UnsentReport = {
  id: string;
  site_id: string;
  site_name?: string | null;
  conducted_date?: string | null;
  submitted_date?: string | null;
  report_generated?: boolean;
  report_url?: string | null;
  /** JIM-1003-05: when a held report will be emailed, and why one was not. */
  send_scheduled_at?: string | null;
  send_error?: string | null;
  /** Jim 10/30: taken off this list by the inspector; stays Not Sent on the site. */
  unsent_dismissed_at?: string | null;
};

type RowState =
  | { status: 'queuing'; report: UnsentReport }
  | {
      status: 'held';
      report: UnsentReport;
      sendsAt: string;
      recipients: Recipient[] | null;
    }
  | { status: 'idle'; report: UnsentReport }
  | {
      status: 'sent';
      report: UnsentReport;
      text: string;
      recipients: Recipient[];
      clientSignature?: ClientSignatureSummary | null;
    }
  | { status: 'error'; report: UnsentReport; text: string };

function dateLabel(report: UnsentReport): string {
  const when = report.conducted_date || report.submitted_date;
  return when ? format(new Date(when), 'MMM d, yyyy') : 'Date unknown';
}

function names(list: Recipient[]): string {
  return list.map((r) => r.name || r.email).join(', ');
}

/**
 * Unsent reports queue. Jim's staging feedback 2026-09-16 ("Unsent Reports"):
 * the receipt belongs under the inspection that was sent, not at the end of
 * the list; the button should read Sending / Stop (red) while it goes, then
 * Sent (green) with who it went to. Each row runs on its own, so tapping down
 * the list sends them together.
 *
 * Send does not email at once (JIM-1003-05, Jim 2026-10-03): the server holds
 * the report for ten minutes and sends it itself. The row shows the
 * recipients and a live clock; Stop cancels the hold. A report queued earlier
 * (from the review screen, or before a reload) comes back from the server
 * with its clock still running.
 */
/** This inspector's completed-but-unsent reports. Shared by the queue and the
 * dashboard's "Needs attention" summary (same key, one fetch). */
export function useUnsentReports(userId?: string) {
  return useQuery({
    queryKey: ['lite-unsent-reports', userId],
    queryFn: async () => {
      const page = await inspectionService.listInspections({
        status: 'submitted',
        inspector_id: userId,
        size: 20,
        skip_count: true,
      });
      // Jim 10/30: a report the inspector took off this list stays off it.
      return (page.items as unknown as UnsentReport[]).filter(
        (r) => !r.unsent_dismissed_at
      );
    },
    enabled: !!userId,
  });
}

/**
 * The clock on one held report. Counts down to `sendsAt`; once there, asks the
 * server how the send went until it answers sent, failed, or stopped.
 */
function HoldClock({
  inspectionId,
  sendsAt,
  onSent,
  onFailed,
  onStopped,
}: {
  inspectionId: string;
  sendsAt: string;
  onSent: (receipt: SendReceipt) => void;
  onFailed: (reason: string) => void;
  onStopped: () => void;
}) {
  const left = useCountdown(sendsAt);
  const due = left === 0;
  // The callbacks change identity on every parent render; the poll must not
  // restart with them.
  const handlers = useRef({ onSent, onFailed, onStopped });
  handlers.current = { onSent, onFailed, onStopped };

  useEffect(() => {
    if (!due) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const check = async () => {
      try {
        const status = await inspectionService.getSendStatus(inspectionId);
        if (cancelled) return;
        if (status.state === 'sent') {
          handlers.current.onSent(
            status.result ?? {
              sent_count: 0,
              recipients: [],
              no_contacts: false,
            }
          );
          return;
        }
        if (status.state === 'failed') {
          handlers.current.onFailed(status.error ?? 'unknown error');
          return;
        }
        if (status.state === 'idle') {
          handlers.current.onStopped();
          return;
        }
      } catch {
        // Offline or a blip: keep asking.
      }
      if (!cancelled) timer = setTimeout(check, SEND_POLL_MS);
    };
    timer = setTimeout(check, 1500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [due, inspectionId]);

  return due ? (
    <span className="inline-flex items-center gap-1.5">
      <Loader2 className="w-3.5 h-3.5 animate-spin" />
      Sending…
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 tabular-nums">
      <Clock className="w-3.5 h-3.5" />
      Sends in {formatCountdown(left)}
    </span>
  );
}

export function UnsentReports({
  userId,
  onRowsChange,
}: {
  userId?: string;
  // Jim 2026-10-08: the receipt flashed for under a second when it was the
  // only report — the dashboard's panel folded as soon as the server list
  // emptied. The dashboard keeps the panel open while rows are showing.
  onRowsChange?: (count: number) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [order, setOrder] = useState<string[]>([]);
  // JIM-1006-01: the stored report address is the private bucket's own, which
  // refuses the browser (Jim 2026-10-06: AccessDenied). Ask the API for a
  // link that works, as the site screen does.
  const [viewingId, setViewingId] = useState<string | null>(null);
  // JIM-1027-13: the contact tool over the queue — "it's in queue already…
  // there's no delete". The send reads the list when it goes.
  const [recipientsFor, setRecipientsFor] = useState<UnsentReport | null>(null);
  // Jim 10/30: Send to client asks first — he sent a July report by accident
  // from this list while looking for the options.
  const [confirmFor, setConfirmFor] = useState<UnsentReport | null>(null);
  const refreshRecipients = (report: UnsentReport) => {
    inspectionService
      .getReportRecipients(report.id)
      .then((r) =>
        setRows((s) => {
          const cur = s[report.id];
          if (cur?.status !== 'held') return s;
          return { ...s, [report.id]: { ...cur, recipients: r.recipients } };
        })
      )
      .catch(() => undefined);
  };
  const viewReport = async (inspectionId: string) => {
    setViewingId(inspectionId);
    try {
      const data = await inspectionService.getReportUrl(inspectionId);
      if (data.has_report && data.report_url) {
        window.open(data.report_url, '_blank');
      }
    } catch (err) {
      console.error('Failed to get report URL:', err);
    } finally {
      setViewingId(null);
    }
  };

  const { data: reports } = useUnsentReports(userId);

  // Rows keep their place: a sent report stays where it was (as its receipt)
  // even after the refetch drops it from the server list.
  useEffect(() => {
    if (!reports) return;
    setOrder((prev) => {
      const keep = prev.filter(
        (id) => reports.some((r) => r.id === id) || rows[id]?.status === 'sent'
      );
      const fresh = reports.map((r) => r.id).filter((id) => !keep.includes(id));
      return [...keep, ...fresh];
    });
    // rows is read, not depended on: a send must not reorder the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports]);

  const setRow = (id: string, next: RowState | null) =>
    setRows((s) => {
      const copy = { ...s };
      if (next) copy[id] = next;
      else delete copy[id];
      return copy;
    });

  const loadRecipients = (report: UnsentReport) =>
    inspectionService
      .getReportRecipients(report.id)
      .then((r) =>
        setRows((s) => {
          const cur = s[report.id];
          if (cur?.status !== 'held') return s;
          return { ...s, [report.id]: { ...cur, recipients: r.recipients } };
        })
      )
      .catch(() => undefined);

  // A report the server is already holding (queued from the review screen,
  // or before this page was reloaded) shows its clock without a tap.
  useEffect(() => {
    for (const report of reports ?? []) {
      if (!report.send_scheduled_at || rows[report.id]) continue;
      setRow(report.id, {
        status: 'held',
        report,
        sendsAt: report.send_scheduled_at,
        recipients: null,
      });
      loadRecipients(report);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports]);

  const showSent = (report: UnsentReport, receipt: SendReceipt) => {
    setRow(report.id, {
      status: 'sent',
      report,
      text: receipt.no_contacts
        ? 'Finalized — no client contacts are set to receive it. Nothing was emailed.'
        : `Sent to ${receipt.sent_count} recipient${receipt.sent_count === 1 ? '' : 's'}`,
      recipients: receipt.recipients ?? [],
      clientSignature: receipt.client_signature ?? null,
    });
    queryClient.invalidateQueries({ queryKey: ['lite-unsent-reports'] });
  };

  const send = useMutation({
    mutationFn: (report: UnsentReport) =>
      inspectionService.queueSend(report.id),
    onMutate: (report) => setRow(report.id, { status: 'queuing', report }),
    onSuccess: (data, report) => {
      if (data.queued) {
        if (!data.sends_at) return setRow(report.id, null);
        setRow(report.id, {
          status: 'held',
          report,
          sendsAt: data.sends_at,
          recipients: null,
        });
        loadRecipients(report);
        return;
      }
      // The hold is switched off on this server: the report went at once.
      showSent(report, {
        sent_count: data.email_results?.sent_count ?? 0,
        recipients: data.email_results?.recipients ?? [],
        no_contacts: data.no_contacts,
        client_signature: data.client_signature ?? null,
      });
    },
    onError: (_err, report) =>
      setRow(report.id, {
        status: 'error',
        report,
        text: 'Send to client failed — try again or contact your admin.',
      }),
  });

  const stop = useMutation({
    mutationFn: (report: UnsentReport) =>
      inspectionService.cancelSend(report.id),
    onSuccess: (_data, report) => {
      // Shown as stopped at once; the refetch then agrees with it.
      setRow(report.id, { status: 'idle', report });
      queryClient.invalidateQueries({ queryKey: ['lite-unsent-reports'] });
    },
    // Too late to stop: the clock runs out and the row becomes its receipt.
  });
  // JIM-1006-02: Edit stops the clock and reopens the inspection on this
  // phone; Send now stops it and emails at once (the Hub's immediate send);
  // Reset timer stops it and queues it again for a fresh ten minutes.
  const editHeld = useMutation({
    mutationFn: async (report: UnsentReport) => {
      await inspectionService.cancelSend(report.id);
      return report;
    },
    onSuccess: (report) => {
      queryClient.invalidateQueries({ queryKey: ['lite-unsent-reports'] });
      navigate(`/lite/inspect/${report.site_id}?edit=${report.id}`);
    },
    onError: (_err, report) =>
      setRow(report.id, {
        status: 'error',
        report,
        text: 'Too late to edit: this report is already being sent.',
      }),
  });
  // Jim 10/30: Remove takes a stale report off this list (two taps); it stays
  // Not Sent on the site and nothing is emailed.
  const dismiss = useMutation({
    mutationFn: (report: UnsentReport) =>
      inspectionService.dismissUnsent(report.id),
    onSuccess: (_data, report) => {
      setRow(report.id, null);
      setOrder((o) => o.filter((id) => id !== report.id));
      queryClient.invalidateQueries({ queryKey: ['lite-unsent-reports'] });
    },
    onError: (_err, report) =>
      setRow(report.id, {
        status: 'error',
        report,
        text: 'Could not remove it — try again.',
      }),
  });
  const sendNow = useMutation({
    mutationFn: async (report: UnsentReport) => {
      await inspectionService.cancelSend(report.id);
      setRow(report.id, { status: 'queuing', report });
      return inspectionService.approveAndSendReport(report.id);
    },
    onSuccess: (data, report) => {
      showSent(report, {
        sent_count: data.email_results?.sent_count ?? 0,
        recipients: data.email_results?.recipients ?? [],
        no_contacts: data.no_contacts,
        client_signature: data.client_signature ?? null,
      });
      queryClient.invalidateQueries({ queryKey: ['lite-unsent-reports'] });
    },
    onError: (_err, report) =>
      setRow(report.id, {
        status: 'error',
        report,
        text: 'Send to client failed. Nothing may have been emailed — check before sending again.',
      }),
  });
  const resetTimer = useMutation({
    mutationFn: async (report: UnsentReport) => {
      await inspectionService.cancelSend(report.id);
      return inspectionService.queueSend(report.id);
    },
    onSuccess: (data, report) => {
      if (!data.queued || !data.sends_at) {
        // The hold is switched off on this server: the report went at once.
        queryClient.invalidateQueries({ queryKey: ['lite-unsent-reports'] });
        return setRow(report.id, null);
      }
      const cur = rows[report.id];
      setRow(report.id, {
        status: 'held',
        report,
        sendsAt: data.sends_at,
        recipients: cur?.status === 'held' ? cur.recipients : null,
      });
    },
    onError: (_err, report) =>
      setRow(report.id, {
        status: 'error',
        report,
        text: 'Could not restart the timer — the report may already be sending.',
      }),
  });

  const byId = new Map((reports ?? []).map((r) => [r.id, r]));
  const visible = order
    .map((id) => rows[id]?.report ?? byId.get(id))
    .filter((r): r is UnsentReport => !!r);
  const visibleCount = visible.length;
  useEffect(() => {
    onRowsChange?.(visibleCount);
  }, [visibleCount, onRowsChange]);
  if (!reports) return null;
  if (visible.length === 0) return null;
  const pending = visible.filter((r) => rows[r.id]?.status !== 'sent').length;
  const allSent = pending === 0;
  return (
    <div
      className={`rounded-xl shadow-soft overflow-hidden border ${
        allSent
          ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
          : 'bg-sky-50 dark:bg-sky-900/20 border-sky-300 dark:border-sky-700'
      }`}
    >
      <div className="px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center flex-shrink-0">
          <FileText className="w-5 h-5 text-sky-600 dark:text-sky-400" />
        </div>
        <div>
          <p className="text-sm font-semibold text-sky-800 dark:text-sky-200">
            {allSent
              ? 'All reports sent to clients.'
              : `${pending} unsent report${pending === 1 ? '' : 's'}`}
          </p>
          {!allSent && (
            <p className="text-xs text-sky-700 dark:text-sky-300 mt-0.5">
              These inspections are complete, but their reports have not been
              sent to the client yet. Tap Send on each — they go out together.
            </p>
          )}
        </div>
      </div>
      <div className="divide-y divide-sky-200 dark:divide-sky-800 border-t border-sky-200 dark:border-sky-800">
        {visible.map((insp) => {
          const state = rows[insp.id];
          const label = `${insp.site_name || 'Inspection'} · ${dateLabel(insp)}`;
          const sent = state?.status === 'sent';
          return (
            <div
              key={insp.id}
              className={`px-4 py-3 ${sent ? 'bg-green-50 dark:bg-green-900/20' : ''}`}
              data-row-state={state?.status ?? 'idle'}
            >
              {/* Jim 2026-10-08: on a phone the four buttons pushed the date
                  into a one-word-per-line column and Stop off the screen;
                  the row stacks there and sits side by side from `sm` up. */}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                <div className="min-w-0">
                  <p
                    className={`text-sm font-medium truncate ${
                      sent
                        ? 'text-green-900 dark:text-green-100'
                        : 'text-sky-900 dark:text-sky-100'
                    }`}
                  >
                    {insp.site_name || 'Inspection'}
                  </p>
                  <p className="text-xs text-sky-700 dark:text-sky-300">
                    {dateLabel(insp)}
                    {insp.report_url && (
                      <>
                        {' · '}
                        <button
                          type="button"
                          onClick={() => viewReport(insp.id)}
                          disabled={viewingId === insp.id}
                          className="underline hover:text-sky-900 dark:hover:text-sky-100 disabled:opacity-60"
                        >
                          {viewingId === insp.id ? 'Opening…' : 'View PDF'}
                        </button>
                      </>
                    )}
                  </p>
                </div>

                {sent ? (
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 text-white text-xs font-semibold flex-shrink-0 self-start sm:self-auto"
                    aria-label={`Sent: ${label}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Sent
                  </span>
                ) : state?.status === 'held' ? (
                  // JIM-1006-02 (Jim 2026-10-06): "if it had buttons for all
                  // possible options it would be great" — Edit, Send to Client
                  // (right now), Reset timer, Stop.
                  <div className="flex flex-wrap items-center gap-1.5 sm:justify-end sm:flex-shrink-0">
                    <button
                      onClick={() => setRecipientsFor(insp)}
                      disabled={stop.isPending}
                      aria-label={`Change who gets it: ${label}`}
                      className="inline-flex items-center gap-1 px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-xs font-semibold disabled:opacity-60"
                    >
                      <Users className="w-3.5 h-3.5" />
                      Recipients
                    </button>
                    <button
                      onClick={() => editHeld.mutate(insp)}
                      disabled={editHeld.isPending || stop.isPending}
                      aria-label={`Edit: ${label}`}
                      className="inline-flex items-center gap-1 px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-xs font-semibold disabled:opacity-60"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Edit
                    </button>
                    <button
                      onClick={() => sendNow.mutate(insp)}
                      disabled={sendNow.isPending || stop.isPending}
                      aria-label={`Send to client now: ${label}`}
                      className="inline-flex items-center gap-1 px-2.5 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold disabled:opacity-60"
                    >
                      <Send className="w-3.5 h-3.5" />
                      Send now
                    </button>
                    <button
                      onClick={() => resetTimer.mutate(insp)}
                      disabled={resetTimer.isPending || stop.isPending}
                      aria-label={`Reset timer: ${label}`}
                      className="inline-flex items-center gap-1 px-2.5 py-2 rounded-lg border border-sky-300 dark:border-sky-700 text-sky-700 dark:text-sky-300 text-xs font-semibold disabled:opacity-60"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Reset timer
                    </button>
                    <button
                      onClick={() => stop.mutate(insp)}
                      disabled={stop.isPending}
                      aria-label={`Stop sending: ${label}`}
                      className="inline-flex items-center gap-1 px-2.5 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold disabled:opacity-60"
                    >
                      <Square className="w-3.5 h-3.5" />
                      Stop
                    </button>
                  </div>
                ) : state?.status === 'queuing' ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-600 text-white text-xs font-semibold flex-shrink-0 self-start sm:self-auto opacity-80">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Queuing…
                  </span>
                ) : (
                  // Jim 10/30: the options sit on the row from the start —
                  // Recipients, Edit, Send to client (which asks first), Remove.
                  <div className="flex flex-wrap items-center gap-1.5 sm:justify-end sm:flex-shrink-0">
                    <button
                      onClick={() => setRecipientsFor(insp)}
                      aria-label={`Change who gets it: ${label}`}
                      className="inline-flex items-center gap-1 px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-xs font-semibold"
                    >
                      <Users className="w-3.5 h-3.5" />
                      Recipients
                    </button>
                    <button
                      onClick={() =>
                        navigate(
                          `/lite/inspect/${insp.site_id}?edit=${insp.id}`
                        )
                      }
                      aria-label={`Edit: ${label}`}
                      className="inline-flex items-center gap-1 px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-xs font-semibold"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Edit
                    </button>
                    <button
                      onClick={() => setConfirmFor(insp)}
                      aria-label={`Send to client: ${label}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold flex-shrink-0 self-start sm:self-auto"
                    >
                      <Send className="w-3.5 h-3.5" />
                      Send to client
                    </button>
                    <DismissButton
                      label="Remove"
                      confirmLabel="Tap again to remove"
                      busyLabel="Removing…"
                      ariaLabel={`Remove from this list: ${label}`}
                      busy={
                        dismiss.isPending && dismiss.variables?.id === insp.id
                      }
                      onConfirm={() => dismiss.mutate(insp)}
                    />
                  </div>
                )}
              </div>

              {state?.status === 'held' && (
                <div
                  className="mt-2 text-xs text-sky-800 dark:text-sky-200"
                  data-testid="send-hold-row"
                >
                  <p className="font-semibold">
                    <HoldClock
                      inspectionId={insp.id}
                      sendsAt={state.sendsAt}
                      onSent={(receipt) => showSent(insp, receipt)}
                      onFailed={(reason) =>
                        setRow(insp.id, {
                          status: 'error',
                          report: insp,
                          text: `Send to client failed: ${reason}. Nothing may have been emailed — check before sending again.`,
                        })
                      }
                      onStopped={() =>
                        setRow(insp.id, { status: 'idle', report: insp })
                      }
                    />
                  </p>
                  <p className="mt-0.5">
                    {state.recipients === null
                      ? 'Checking who receives this report…'
                      : state.recipients.length === 0
                        ? 'No client contacts are set to receive it — sending finalizes the report without emailing anyone.'
                        : `Sending to ${names(state.recipients)}`}
                    <span className="text-sky-600 dark:text-sky-400">
                      {' '}
                      · goes out at {sendsAtLabel(state.sendsAt)} — Stop to hold
                      it
                    </span>
                  </p>
                </div>
              )}
              {state?.status === 'sent' && (
                <div className="mt-2 text-xs text-green-800 dark:text-green-300">
                  <p className="font-medium">{state.text}</p>
                  {state.recipients.length > 0 && (
                    <ul className="mt-0.5 space-y-0.5">
                      {state.recipients.map((p) => (
                        <li key={p.email}>
                          {p.name ? `${p.name} · ${p.email}` : p.email}
                          {p.status && p.status !== 'sent'
                            ? ` (${p.status})`
                            : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                  {clientSignatureLine(state.clientSignature) && (
                    <p
                      data-testid="client-signature-line"
                      className="mt-1 text-slate-700 dark:text-slate-300"
                    >
                      {clientSignatureLine(state.clientSignature)}
                    </p>
                  )}
                </div>
              )}
              {state?.status === 'error' && (
                <p className="mt-2 text-xs font-medium text-red-700 dark:text-red-400">
                  {state.text}
                </p>
              )}
              {!state && insp.send_error && (
                <p className="mt-2 text-xs font-medium text-red-700 dark:text-red-400">
                  Last send did not go: {insp.send_error}
                </p>
              )}
            </div>
          );
        })}
      </div>
      {confirmFor && (
        <SendConfirmSheet
          inspectionId={confirmFor.id}
          siteId={confirmFor.site_id}
          editLabel="Not now"
          onConfirm={() => {
            const report = confirmFor;
            setConfirmFor(null);
            send.mutate(report);
          }}
          onEdit={() => setConfirmFor(null)}
        />
      )}
      {recipientsFor && (
        <ReportRecipientsEditor
          siteId={recipientsFor.site_id}
          inspectionId={recipientsFor.id}
          onClose={() => {
            const report = recipientsFor;
            setRecipientsFor(null);
            refreshRecipients(report);
          }}
        />
      )}
    </div>
  );
}
